import {
    type CSSProperties,
    useState,
    useRef
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type DateTimePickerDateRange,
    type IMonthSelectorRef,
    NCoreUIKitLocalize,
    formatDateTime,
    MonthSelector,
    RadioButton,
    Button,
    Switch
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "MonthSelector";

const USAGE_CODE = `const [date, setDate] = useState<Date>();

<MonthSelector
    selectMultipleObject={() => undefined}
    selectObject={setDate}
    variant="single"
    date={date}
/>`;

const RANGE_CODE = `const [dateRange, setDateRange] = useState<DateTimePickerDateRange>();

<MonthSelector
    selectMultipleObject={setDateRange}
    selectObject={() => undefined}
    multipleSelectDayLimit={6}
    dateRange={dateRange}
    variant="range"
/>`;

const LIMITS_CODE = `<MonthSelector
    selectMultipleObject={() => undefined}
    maxDate={new Date(2027, 0, 31)}
    minDate={new Date(2026, 7, 1)}
    selectObject={setDate}
    variant="single"
    date={date}
/>`;

const REF_CODE = `const monthRef = useRef<IMonthSelectorRef>(null);

<MonthSelector
    selectMultipleObject={() => undefined}
    selectObject={setDate}
    ref={monthRef}
    variant="single"
    date={date}
/>

monthRef.current?.changeCurrentYear(new Date());`;

const NAME_LENGTHS = [
    "short",
    "long"
] as const;

const NAME_LENGTH_KEYS = {
    short: "monthSelectorPage.lengthShort",
    long: "monthSelectorPage.lengthLong"
} as const;

const CONTROLS_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 12
};

const MONTH_FORMAT: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long"
};

const TODAY = new Date();

const addMonths = (date: Date, count: number) => new Date(date.getFullYear(), date.getMonth() + count, 1);

const addYears = (date: Date, count: number) => new Date(date.getFullYear() + count, date.getMonth(), 1);

const RANGE_MAX_MONTHS = 6;

const LIMIT_MIN_DATE = addMonths(TODAY, -2);

const LIMIT_MAX_DATE = addMonths(TODAY, 3);

const JUMP_YEARS = 5;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("monthSelectorPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("monthSelectorPage.limitsTitle"),
        id: "limits"
    },
    {
        title: t("monthSelectorPage.displayTitle"),
        id: "display"
    },
    {
        title: t("monthSelectorPage.viewTitle"),
        id: "view"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const MonthSelectorPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const monthRef = useRef<IMonthSelectorRef>(null);

    const [
        date,
        setDate
    ] = useState<Date | undefined>(undefined);

    const [
        dateRange,
        setDateRange
    ] = useState<DateTimePickerDateRange | undefined>(undefined);

    const [
        limitDate,
        setLimitDate
    ] = useState<Date | undefined>(undefined);

    const [
        displayDate,
        setDisplayDate
    ] = useState<Date | undefined>(undefined);

    const [
        viewedDate,
        setViewedDate
    ] = useState<Date | undefined>(undefined);

    const [
        nameLength,
        setNameLength
    ] = useState<(typeof NAME_LENGTHS)[number]>("short");

    const [
        isTodayShown,
        setIsTodayShown
    ] = useState(true);

    const formatMonth = (value: Date) => formatDateTime({
        options: MONTH_FORMAT,
        locale: activeLocale,
        date: value
    });

    const getDateCaption = () => {
        if(!date) {
            return t("monthSelectorPage.overviewCaption");
        }

        return t("monthSelectorPage.selectedCaption", [
            formatMonth(date)
        ]);
    };

    const getRangeCaption = () => {
        if(!dateRange?.start) {
            return t("monthSelectorPage.rangeEmptyCaption", [
                RANGE_MAX_MONTHS
            ]);
        }

        if(!dateRange.end) {
            return t("monthSelectorPage.rangeStartCaption", [
                formatMonth(dateRange.start)
            ]);
        }

        return t("monthSelectorPage.rangeCaption", [
            formatMonth(dateRange.start),
            formatMonth(dateRange.end)
        ]);
    };

    const getLimitCaption = () => {
        if(!limitDate) {
            return t("monthSelectorPage.limitsCaption", [
                formatMonth(LIMIT_MIN_DATE),
                formatMonth(LIMIT_MAX_DATE)
            ]);
        }

        return t("monthSelectorPage.selectedCaption", [
            formatMonth(limitDate)
        ]);
    };

    const handleThisYear = () => {
        monthRef.current?.changeCurrentYear(new Date());
    };

    const handleJumpAhead = () => {
        monthRef.current?.changeCurrentYear(addYears(TODAY, JUMP_YEARS));
    };

    return <Article>
        <ComponentHeader
            lead={t("monthSelectorPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("monthSelectorPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("monthSelectorPage.overviewValueText")}
            </Paragraph>
            <Preview
                caption={getDateCaption()}
            >
                <MonthSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setDate}
                    variant="single"
                    date={date}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("monthSelectorPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("monthSelectorPage.rangeText")}
            </Paragraph>
            <Preview
                caption={getRangeCaption()}
            >
                <MonthSelector
                    multipleSelectDayLimit={RANGE_MAX_MONTHS}
                    selectMultipleObject={setDateRange}
                    selectObject={() => undefined}
                    dateRange={dateRange}
                    variant="range"
                />
            </Preview>
            <CodeBlock
                code={RANGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("monthSelectorPage.limitsTitle")}
            id="limits"
        >
            <Paragraph>
                {t("monthSelectorPage.limitsText")}
            </Paragraph>
            <Preview
                caption={getLimitCaption()}
            >
                <MonthSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setLimitDate}
                    maxDate={LIMIT_MAX_DATE}
                    minDate={LIMIT_MIN_DATE}
                    date={limitDate}
                    variant="single"
                />
            </Preview>
            <CodeBlock
                code={LIMITS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("monthSelectorPage.displayTitle")}
            id="display"
        >
            <Paragraph>
                {t("monthSelectorPage.displayText")}
            </Paragraph>
            <Preview>
                <MonthSelector
                    selectMultipleObject={() => undefined}
                    isShowTodayIndicator={isTodayShown}
                    monthOfYearLengthType={nameLength}
                    selectObject={setDisplayDate}
                    date={displayDate}
                    variant="single"
                />
                <div
                    style={CONTROLS_STYLE}
                >
                    <Switch
                        title={t("monthSelectorPage.todayMarker")}
                        onCheckedChange={setIsTodayShown}
                        isChecked={isTodayShown}
                    />
                    <div
                        aria-label={t("monthSelectorPage.lengthLabel")}
                        role="radiogroup"
                    >
                        {
                            NAME_LENGTHS.map((length) => <RadioButton
                                onPress={() => setNameLength(length)}
                                title={t(NAME_LENGTH_KEYS[length])}
                                isChecked={nameLength === length}
                                key={length}
                            />)
                        }
                    </div>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("monthSelectorPage.viewTitle")}
            id="view"
        >
            <Paragraph>
                {t("monthSelectorPage.viewText")}
            </Paragraph>
            <Preview>
                <MonthSelector
                    selectMultipleObject={() => undefined}
                    selectObject={setViewedDate}
                    date={viewedDate}
                    variant="single"
                    ref={monthRef}
                />
                <div
                    style={CONTROLS_STYLE}
                >
                    <Button
                        title={t("monthSelectorPage.goThisYear")}
                        onPress={handleThisYear}
                        variant="outline"
                        type="neutral"
                    />
                    <Button
                        title={t("monthSelectorPage.goAhead", [
                            JUMP_YEARS
                        ])}
                        onPress={handleJumpAhead}
                        variant="outline"
                        type="neutral"
                    />
                </div>
            </Preview>
            <CodeBlock
                code={REF_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("monthSelectorPage.a11yStructure"),
                    t("monthSelectorPage.a11yMonth"),
                    t("monthSelectorPage.a11yState"),
                    t("monthSelectorPage.a11yYear"),
                    t("monthSelectorPage.a11yKeyboard"),
                    t("monthSelectorPage.a11yLimit"),
                    t("monthSelectorPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("monthSelectorPage.propVariant"),
                        type: "\"single\" | \"range\" | \"rrule\"",
                        name: "variant"
                    },
                    {
                        description: t("monthSelectorPage.propSelectObject"),
                        type: "(date?: Date) => void",
                        name: "selectObject"
                    },
                    {
                        description: t("monthSelectorPage.propSelectMultipleObject"),
                        type: "(dateRange?: DateTimePickerDateRange) => void",
                        name: "selectMultipleObject"
                    },
                    {
                        description: t("monthSelectorPage.propDate"),
                        name: "date",
                        type: "Date"
                    },
                    {
                        description: t("monthSelectorPage.propDateRange"),
                        type: "DateTimePickerDateRange",
                        name: "dateRange"
                    },
                    {
                        description: t("monthSelectorPage.propViewDate"),
                        name: "viewDate",
                        type: "Date"
                    },
                    {
                        description: t("monthSelectorPage.propMinDate"),
                        name: "minDate",
                        type: "Date"
                    },
                    {
                        description: t("monthSelectorPage.propMaxDate"),
                        name: "maxDate",
                        type: "Date"
                    },
                    {
                        description: t("monthSelectorPage.propMultipleSelectDayLimit"),
                        name: "multipleSelectDayLimit",
                        type: "number"
                    },
                    {
                        description: t("monthSelectorPage.propMultipleSelectMinimumRequiredDayCount"),
                        name: "multipleSelectMinimumRequiredDayCount",
                        type: "number"
                    },
                    {
                        description: t("monthSelectorPage.propMonthOfYearLengthType"),
                        name: "monthOfYearLengthType",
                        type: "\"short\" | \"long\"",
                        defaultValue: "\"short\""
                    },
                    {
                        description: t("monthSelectorPage.propIsShowTodayIndicator"),
                        name: "isShowTodayIndicator",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("monthSelectorPage.propIsShowCard"),
                        defaultValue: "true",
                        name: "isShowCard",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("monthSelectorPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("monthSelectorPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                {t("monthSelectorPage.propsRefIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("monthSelectorPage.refChangeCurrentYear"),
                        type: "(date: Date) => void",
                        name: "changeCurrentYear"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("monthSelectorPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MonthSelectorPage;
